const toLabel = key => {
    if (!key) return ''
    return String(key)
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, match => match.toUpperCase())
}

const normalizeNumber = value => {
    if (value === null || value === undefined || value === '') return null
    const normalized = String(value).replace(/,/g, '')
    const number = Number(normalized)
    return Number.isNaN(number) ? null : number
}

export const formatNumber = value => {
    if (value === null || value === undefined || value === '') return ''
    const number = typeof value === 'number' ? value : normalizeNumber(value)
    if (number === null) return ''
    let formatted = number.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
    })
    formatted = formatted.replace(/(\.[0-9]*[1-9])0+$/, '$1').replace(/\.0+$/, '')
    return formatted
}

const isPercentKey = key => String(key).toLowerCase().includes('percent')

const sumNumeric = (records, key) => {
    if (!Array.isArray(records) || records.length === 0) return null
    let total = 0
    let hasValue = false
    records.forEach(record => {
        const value = normalizeNumber(record ? record[key] : null)
        if (value !== null) {
            total += value
            hasValue = true
        }
    })
    return hasValue ? total : null
}

const getNumericKeys = (records, columns) => {
    if (!Array.isArray(columns)) return []
    return columns
        .filter(column => column.summarize !== false)
        .map(column => column.key)
        .filter(key => {
            const total = sumNumeric(records, key)
            return total !== null
        })
}

export const buildWarehouseReportColumns = records => {
    if (!Array.isArray(records) || records.length === 0) return []
    const sample = records[0] || {}
    return Object.keys(sample).map(key => ({
        key,
        label: toLabel(key),
        sortable: true,
    }))
}

export const buildWarehouseReportTotalsRow = (records, columns) => {
    if (!Array.isArray(records) || records.length === 0) return null
    if (!Array.isArray(columns) || columns.length === 0) return null

    const totals = { summaryRow: true }
    const numericKeys = getNumericKeys(records, columns)

    const [{ key: firstColumnKey }] = columns
    totals[firstColumnKey] = 'Total'

    const weightLossKey = columns.find(col => String(col.key).toLowerCase() === 'weight_loss')?.key
    const purchaseMassKey = columns.find(col => String(col.key).toLowerCase() === 'purchase_mass')?.key
    const weightLossTotal = weightLossKey ? sumNumeric(records, weightLossKey) : null
    const purchaseMassTotal = purchaseMassKey ? sumNumeric(records, purchaseMassKey) : null
    const overallLossPercent = (weightLossTotal !== null && purchaseMassTotal)
        ? (weightLossTotal / purchaseMassTotal) * 100
        : null

    columns.forEach(column => {
        const { key } = column
        if (key === firstColumnKey) return

        if (isPercentKey(key)) {
            totals[key] = overallLossPercent !== null ? formatNumber(overallLossPercent) : ''
            return
        }

        if (!numericKeys.includes(key)) {
            totals[key] = ''
            return
        }

        const total = sumNumeric(records, key)
        totals[key] = total !== null ? formatNumber(total) : ''
    })

    return totals
}

export const buildWarehouseReportSummary = (records, columns) => {
    if (!Array.isArray(records) || records.length === 0) return null
    if (!Array.isArray(columns) || columns.length === 0) return null

    const numericKeys = getNumericKeys(records, columns)
    const weightLossKey = columns.find(col => String(col.key).toLowerCase() === 'weight_loss')?.key
    const purchaseMassKey = columns.find(col => String(col.key).toLowerCase() === 'purchase_mass')?.key
    const weightLossTotal = weightLossKey ? sumNumeric(records, weightLossKey) : null
    const purchaseMassTotal = purchaseMassKey ? sumNumeric(records, purchaseMassKey) : null
    const overallLossPercent = (weightLossTotal !== null && purchaseMassTotal)
        ? (weightLossTotal / purchaseMassTotal) * 100
        : null
    const summary = {
        totalRecords: records.length,
        totals: {},
    }

    numericKeys.forEach(key => {
        if (isPercentKey(key)) {
            summary.totals[key] = overallLossPercent !== null ? formatNumber(overallLossPercent) : ''
        } else {
            summary.totals[key] = formatNumber(sumNumeric(records, key))
        }
    })

    return summary
}
